import { Autocomplete, Box, IconButton, Paper, TextField, Typography } from '@mui/material';
import type { IngestedEventSample } from '@vy/types';
import { VyIcon } from '@vy/ui-design-system';
import {
  extract,
  fromNullToUndefined,
  isEmptyString,
  isNotNullOrUndefined,
  vyDate,
  vySerialize,
  withDefault,
} from '@vy/utils';
import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { VyRangePicker } from '../../../components/new-design/inputs/VyRangePicker';
import { VyLayout } from '../../../components/new-design/layout/VyLayout';
import { VyTableToolbar } from '../../../components/new-design/table/vy-table-toolbar/vy-table-toolbar';
import { withGlobalSearchParams } from '../../../components/utility/GlobalSearchParamsBoundary';
import { useEventNames } from '../../../hooks/requests/use-event-names';
import { useFromDayParam } from '../../../hooks/search-params/use-from-day-param';
import { useTablePagination } from '../../../hooks/use-table-pagination';
import { useToDayParam } from '../../../hooks/search-params/use-to-day-param';
import { useDialog } from '../../../hooks/use-dialog';
import { usePaginationResetHandler } from '../../../hooks/use-pagination-reset-handler';
import { useStateParams } from '../../../hooks/use-state-params';
import { deserializeStringArrayParam, serializeStringArrayParam } from '../../../utils/params-formatting';
import { EventDataDrawer } from './events-page-components/DataEventDrawer';
import { EventsTable } from './events-page-components/EventsTable/EventsTable';
import { useEventsActions } from './events-page-hooks/use-events-actions';
import { useLatestEvents, UseLatestEventsPayload } from '../../../hooks/requests/use-latest-events';
const EventsPage: React.FC = () => {
  const { page, pageSize, onPageChange, onPageSizeChange } = useTablePagination();

  const defaultFromDay = vyDate().startOf('day');
  const defaultToDay = vyDate().endOf('day');

  const [paramsDayFrom] = useFromDayParam(defaultFromDay);
  const [paramsDayTo] = useToDayParam(defaultToDay);

  const fromDay = paramsDayFrom ?? defaultFromDay;
  const toDay = paramsDayTo ?? defaultToDay;

  const startTime = fromDay.utc(true).toDate();
  const endTime = toDay.utc(true).toDate();

  const { data } = useEventNames();
  const eventNames = extract(withDefault(data?.eventNames, []), 'eventName');

  const [selectedEventNames, setEventNames] = useStateParams<string[]>(
    [],
    'eventNames',
    serializeStringArrayParam,
    deserializeStringArrayParam,
  );
  const [customerAlias, setCustomerAlias] = useStateParams<string | undefined>(
    undefined,
    'customerAlias',
    (aliasValue) => aliasValue,
    (aliasValue) => fromNullToUndefined(aliasValue),
  );
  const [ref, setRef] = useStateParams<string | undefined>(
    undefined,
    'ref',
    (refValue) => refValue,
    (refValue) => fromNullToUndefined(refValue),
  );

  const normalizedRef = fromNullToUndefined(ref);
  const normalizedCustomerAlias = fromNullToUndefined(customerAlias);
  const isFilteringByRef = isNotNullOrUndefined(normalizedRef);

  const serializedEventNames = vySerialize(selectedEventNames ?? []);

  const filterInfo: Exclude<UseLatestEventsPayload, 'page' | 'pageSize'> = isFilteringByRef
    ? { ref: normalizedRef }
    : {
        startTime,
        endTime,
        eventNames: serializedEventNames,
        customerAlias: normalizedCustomerAlias,
      };

  const latestEventsQueryParams: UseLatestEventsPayload = {
    ...filterInfo,
    page,
    pageSize,
  };

  const { data: latestEventsResponse, isFetching: isFetchingEvents } =
    useLatestEvents(latestEventsQueryParams);

  const { handleExportEvents, isExporting } = useEventsActions({ filterInfo });
  const {
    isDialogOpen: isDataDrawerOpen,
    handleOpen: openDataDrawer,
    handleClose: closeDataDrawer,
  } = useDialog();
  const [selectedEvent, setSelectedEvent] = useState<IngestedEventSample | undefined>(undefined);

  const events = latestEventsResponse?.events ?? [];

  usePaginationResetHandler(withDefault(latestEventsResponse?.countedFilteredEvents, 0));

  const handleSelectEvent = (event: IngestedEventSample) => {
    setSelectedEvent(event);
    openDataDrawer();
  };

  return (
    <>
      <Helmet>
        <title>Events</title>
      </Helmet>
      <VyLayout
        sx={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Typography
          sx={{
            fontSize: 24,
            padding: 1.5
          }}>
          Events
        </Typography>
        <Paper
          variant="outlined"
          sx={(theme) => ({
            borderRadius: '12px',
            boxShadow: theme.shadows[1],
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          })}
        >
          <EventDataDrawer
            dataDrawerOpen={isDataDrawerOpen}
            closeDataDrawer={closeDataDrawer}
            event={selectedEvent}
            startTime={startTime}
            endTime={endTime}
          />

          <VyTableToolbar sx={{ padding: '24px 16px', flexWrap: 'wrap', rowGap: '10px' }}>
            <VyTableToolbar.Queries
              sx={{
                alignItems: 'start',
                flexWrap: 'wrap',
                rowGap: '10px',
              }}
            >

              <VyRangePicker
                size="medium"
                textFieldProps={{
                  disabled: isFilteringByRef,
                }}
              />

              <div>
                <Autocomplete
                  multiple
                  size="medium"
                  limitTags={1}
                  options={eventNames}
                  onChange={(_, value) => setEventNames(value)}
                  value={eventNames.filter((eventName) =>
                    withDefault(selectedEventNames, []).includes(eventName),
                  )}
                  disabled={isFilteringByRef}
                  sx={{
                    width: '240px',
                  }}
                  renderInput={(params) => <TextField {...params} label="Events" fullWidth />}
                  disableClearable
                />
              </div>

              <TextField
                size="medium"
                label="Ref"
                value={normalizedRef ?? ''}
                onChange={(event) => {
                  const nextValue = event.target.value.trim();

                  setRef(isEmptyString(nextValue) ? undefined : nextValue);
                }}
                sx={{
                  width: '240px',
                }}
              />

              <TextField
                size="medium"
                label="Customer Alias"
                value={normalizedCustomerAlias ?? ''}
                onChange={(event) => {
                  const nextValue = event.target.value.trim();

                  setCustomerAlias(isEmptyString(nextValue) ? undefined : nextValue);
                }}
                disabled={isFilteringByRef}
                sx={{
                  width: '240px',
                }}
              />
            </VyTableToolbar.Queries>
            <VyTableToolbar.Actions>
              <IconButton
                aria-label="Export events as CSV"
                onClick={handleExportEvents}
                disabled={isExporting}
                sx={{
                  border: '1px solid #0D1B273B',
                  borderRadius: '4px',
                  padding: '8px',
                  width: '36px',
                  height: '36px',
                  '&:hover': {
                    backgroundColor: '#F5F6FA',
                  },
                }}
              >
                <VyIcon
                  icon="Download"
                  style={{
                    transform: 'scale(0.8)',
                  }}
                />
              </IconButton>
            </VyTableToolbar.Actions>
          </VyTableToolbar>

          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <EventsTable
              items={events}
              loadingItems={isFetchingEvents}
              itemsCount={withDefault(latestEventsResponse?.countedFilteredEvents, 0)}
              onRowClick={(row) => handleSelectEvent(row.row as IngestedEventSample)}
              page={page}
              pageSize={pageSize}
              onPageChange={onPageChange}
              onPageSizeChange={onPageSizeChange}
            />
          </Box>
        </Paper>
      </VyLayout>
    </>
  );
};

const EventsPageWithSuspenseBoundary = withGlobalSearchParams(EventsPage);

export { EventsPageWithSuspenseBoundary as EventsPage };
